import type { ActStatus, CheckoutProblemKind, InvoiceStatus, ProjectHealth, SecretStatus, Task, TaskStatus } from '@/api/types';
import type { Tone } from '@/components/ui';

type Label = { tone: Tone; label: string };

export const health: Record<ProjectHealth, Label> = {
  on_track: { tone: 'done', label: 'Идёт по плану' },
  waiting_client: { tone: 'waiting', label: 'Ждём вас' },
  at_risk: { tone: 'overdue', label: 'Есть риск по срокам' },
  paused: { tone: 'neutral', label: 'На паузе' },
  done: { tone: 'neutral', label: 'Завершён' },
};

export const taskStatus: Record<TaskStatus, Label> = {
  backlog: { tone: 'neutral', label: 'Позже' },
  todo: { tone: 'neutral', label: 'Следующая' },
  in_progress: { tone: 'active', label: 'В работе' },
  review: { tone: 'active', label: 'На согласовании' },
  done: { tone: 'done', label: 'Готово' },
};

export const statusOrder: TaskStatus[] = ['review', 'in_progress', 'todo', 'backlog', 'done'];

/** A task waiting for the client outranks its own workflow status. */
export function taskLabel(t: Task): Label {
  if (t.needsClient && t.status !== 'done') return { tone: 'waiting', label: needsLabel[t.needsClient.kind] };
  return taskStatus[t.status];
}

export const needsLabel: Record<NonNullable<Task['needsClient']>['kind'], string> = {
  approve: 'Ждёт утверждения',
  answer: 'Ждёт ответа',
  materials: 'Ждёт материалов',
  access: 'Ждёт доступа',
};

export const invoiceStatus: Record<InvoiceStatus, Label> = {
  draft: { tone: 'neutral', label: 'Черновик' },
  sent: { tone: 'waiting', label: 'К оплате' },
  processing: { tone: 'active', label: 'Проверяем оплату' },
  paid: { tone: 'done', label: 'Оплачен' },
  overdue: { tone: 'overdue', label: 'Просрочен' },
  cancelled: { tone: 'neutral', label: 'Отменён' },
};

export const actStatus: Record<ActStatus, Label> = {
  draft: { tone: 'neutral', label: 'Черновик' },
  to_sign: { tone: 'waiting', label: 'Ждёт подписи' },
  signed: { tone: 'done', label: 'Подписан' },
  void: { tone: 'neutral', label: 'Отозван' },
};

export const signedViaLabel = { portal: 'в кабинете', scan: 'по скану от клиента', paper: 'на бумаге', edo: 'в ЭДО' } as const;

export const secretStatus: Record<SecretStatus, Label> = {
  waiting: { tone: 'waiting', label: 'Не открыт' },
  opened: { tone: 'done', label: 'Открыт' },
  expired: { tone: 'neutral', label: 'Истёк' },
  revoked: { tone: 'neutral', label: 'Отозван' },
};

export const methodLabel = { card: 'картой', sbp: 'через СБП', transfer: 'переводом', cash: 'наличными' } as const;

export const manualMethodLabel = { transfer: 'Перевод', cash: 'Наличные', card: 'Карта', other: 'Другое' } as const;

export const problemLabel: Record<CheckoutProblemKind, string> = {
  amount_mismatch: 'банк прислал другую сумму',
  overpaid: 'оплачено больше, чем счёт',
  record_failed: 'деньги пришли, но не записались в учёт',
  paid_after_expiry: 'оплата пришла после истечения ссылки',
};
